// Carimbo impresso NO DOWNLOAD (pedido do Lucas 2026-07-20): a foto fica limpa
// no armazenamento; na hora que o escritório baixa, a tarja com data/hora +
// coordenadas (metadados que já vêm da listagem /mobile/*) é desenhada no
// navegador via canvas. O fetch dos bytes exige CORS no bucket — já permitido
// (os ZIPs de fotos usam o mesmo caminho); o canvas desenha de um blob local,
// então não há taint. Mesma tarja do PWA (lib/fotos.ts do pwa-campo).
import { saveAs } from 'file-saver';

export interface FotoCarimbavel {
  url: string;
  nome_original?: string | null;
  lat?: number | string | null;
  lon?: number | string | null;
  accuracy_m?: number | string | null;   // PG numeric chega como string
  capturado_em?: string | null;
}

function linhasCarimbo(f: FotoCarimbavel): string[] {
  const d = f.capturado_em ? new Date(f.capturado_em) : new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const out = [`${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`];
  const lat = f.lat != null ? Number(f.lat) : NaN;
  const lon = f.lon != null ? Number(f.lon) : NaN;
  if (!isNaN(lat) && !isNaN(lon)) {
    const accN = f.accuracy_m != null ? Number(f.accuracy_m) : NaN;
    const acc = !isNaN(accN) ? ` (±${Math.round(accN)} m)` : '';
    out.push(`${lat.toFixed(5)}, ${lon.toFixed(5)}${acc}`);
  }
  return out;
}

function desenharCarimbo(ctx: CanvasRenderingContext2D, w: number, h: number, linhas: string[]) {
  const fs = Math.max(16, Math.round(Math.min(w, h) / 32));
  ctx.font = `bold ${fs}px sans-serif`;
  const pad = Math.round(fs * 0.6);
  const lh = Math.round(fs * 1.3);
  const larg = Math.max(...linhas.map((l) => ctx.measureText(l).width));
  const bw = larg + pad * 2;
  const bh = lh * linhas.length + pad;
  const x = w - bw - pad;
  const y = h - bh - pad;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(x, y, bw, bh);
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'top';
  linhas.forEach((l, i) => ctx.fillText(l, x + pad, y + pad * 0.5 + i * lh));
}

// Recebe os bytes já baixados e devolve o JPEG com o carimbo queimado.
export async function carimbarBlob(blob: Blob, f: FotoCarimbavel): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error('não consegui decodificar a imagem'));
      im.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d indisponível');
    ctx.drawImage(img, 0, 0);
    desenharCarimbo(ctx, w, h, linhasCarimbo(f));
    const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
    if (!out) throw new Error('falha ao gerar o JPEG carimbado');
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function nomeCarimbado(nomeOriginal?: string | null, fallback = 'foto'): string {
  const base = (nomeOriginal || fallback).replace(/\.[^.]+$/, '');
  return `${base}-carimbada.jpg`;
}

// Baixa UMA foto com o carimbo impresso.
export async function baixarFotoCarimbada(f: FotoCarimbavel): Promise<void> {
  const resp = await fetch(f.url, { mode: 'cors', cache: 'no-store' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const carimbada = await carimbarBlob(await resp.blob(), f);
  saveAs(carimbada, nomeCarimbado(f.nome_original));
}
